/**
 * src/beta/betaTests.js
 * Beta test definitions for SeaSuite beta testers.
 * Each test has an id, title, description (high-level), and ordered steps.
 * Tests added after the initial 20 have `isNew: true` so the UI can badge them.
 */

const BETA_TESTS = [
  {
    id: 'bt-01',
    title: 'Login & Authentication',
    description: 'Verify that tenant users can log in, see their session, and log out correctly.',
    steps: [
      { id: 's1', text: 'Open your tenant URL (e.g. yourcompany.tryggr.dk) in a browser.' },
      { id: 's2', text: 'Enter your username and password on the login screen.' },
      { id: 's3', text: 'Verify you see the Dashboard after logging in.' },
      { id: 's4', text: 'Check the sidebar shows your display name and role.' },
      {
        id: 's5',
        text: 'Click "logout" in the sidebar user card and confirm you return to the login screen.',
      },
    ],
  },
  {
    id: 'bt-02',
    title: 'Dashboard Overview',
    description:
      'Check that the Dashboard loads correctly and displays relevant compliance metrics.',
    steps: [
      { id: 's1', text: 'Navigate to Dashboard from the sidebar.' },
      { id: 's2', text: 'Verify the compliance health score widget is visible.' },
      { id: 's3', text: 'Check that the historical trend sparkline renders.' },
      { id: 's4', text: 'Confirm the KPI cards (documents, tasks, risks) show correct counts.' },
      { id: 's5', text: 'Resize your browser window and verify the dashboard remains usable.' },
    ],
  },
  {
    id: 'bt-03',
    title: 'Create a Document',
    description: 'Test the full document creation flow from draft to saving.',
    steps: [
      { id: 's1', text: 'Navigate to Documents from the sidebar.' },
      { id: 's2', text: 'Click the "+ New Document" button.' },
      { id: 's3', text: 'Fill in the document title, type, and any required fields.' },
      { id: 's4', text: 'Write some content in the document editor.' },
      {
        id: 's5',
        text: 'Save the document and verify it appears in the document list with "Draft" status.',
      },
    ],
  },
  {
    id: 'bt-04',
    title: 'Document Approval Workflow',
    description:
      'Test the document lifecycle from draft through review to published, including version snapshot archiving.',
    steps: [
      { id: 's1', text: 'Open an existing Draft document from the Documents list.' },
      {
        id: 's2',
        text: 'Open the Workflow tab and click "Submit for Review" to send the document into review.',
      },
      {
        id: 's3',
        text: 'Verify the document now shows "In Review" status in the lifecycle badge.',
      },
      {
        id: 's4',
        text: 'If you are the approver, open the approval modal and approve the document.',
      },
      { id: 's5', text: 'Confirm the document status changes to "Published".' },
      {
        id: 's6',
        text: 'With the document Published, click "Save Snapshot" (or "Archive version") to archive the current version. Enter a version label, confirm, and verify the snapshot appears in the Version History panel.',
      },
      {
        id: 's7',
        text: 'Make an edit to the published document and save it — confirm the version counter increments and the previous snapshot is still visible in the version history.',
      },
    ],
  },
  {
    id: 'bt-05',
    title: 'Task System Overview',
    description:
      'Explore the redesigned 4-tab task system — obligations, domain actions, ad-hoc tasks, and the obligation catalog.',
    steps: [
      { id: 's1', text: 'Navigate to Tasks from the sidebar.' },
      {
        id: 's2',
        text: 'Verify the page loads with four tabs: Obligations, Actions, Ad-Hoc, and Obligation Catalog.',
      },
      {
        id: 's3',
        text: 'Click the Obligations tab — verify materialised obligation tasks are listed (or an empty state if none are enabled).',
      },
      {
        id: 's4',
        text: 'Click the Actions tab — verify domain actions from risks are aggregated here (or empty state).',
      },
      {
        id: 's5',
        text: 'Click the Ad-Hoc tab — verify ad-hoc tasks are listed with status and assignee columns.',
      },
      {
        id: 's6',
        text: 'Click the Obligation Catalog tab — verify the obligation catalog loads with enable/disable toggles.',
      },
      {
        id: 's7',
        text: 'Use the status and assignee filter dropdowns on the Obligations or Ad-Hoc tab and verify filtering works.',
      },
    ],
  },
  {
    id: 'bt-06',
    title: 'Controls Review',
    description: 'Browse and inspect compliance controls and their evaluation status.',
    steps: [
      { id: 's1', text: 'Navigate to Controls from the sidebar.' },
      {
        id: 's2',
        text: 'Browse the list of controls — verify they load and show framework labels.',
      },
      { id: 's3', text: 'Click on a control to open the detail modal.' },
      {
        id: 's4',
        text: 'Review the 5-gate evaluation status (documents, SLA, acknowledgment, evidence, obligations).',
      },
      { id: 's5', text: 'Link a document or artefact to the control if possible.' },
      { id: 's6', text: 'Close the modal and verify the control status updated in the list.' },
    ],
  },
  {
    id: 'bt-07',
    title: 'Risk Register — Create a Risk',
    description: 'Test creating a new risk entry using the risk wizard.',
    steps: [
      { id: 's1', text: 'Navigate to Risks from the sidebar.' },
      { id: 's2', text: 'Click "+ New Risk" to open the Risk Wizard.' },
      { id: 's3', text: 'Fill in the risk title, description, and category.' },
      { id: 's4', text: 'Set the inherent likelihood and impact scores.' },
      { id: 's5', text: 'Add a treatment plan with actions and set residual scores.' },
      { id: 's6', text: 'Save the risk and verify it appears in the risk register.' },
    ],
  },
  {
    id: 'bt-08',
    title: 'Risk Assessment & Scoring',
    description: 'Validate risk scoring, heat map, and score history tracking.',
    steps: [
      { id: 's1', text: 'Open an existing risk from the Risk Register.' },
      { id: 's2', text: 'Review the inherent and residual score calculations.' },
      { id: 's3', text: 'Take a score snapshot (if available) to track score history.' },
      { id: 's4', text: 'Change the risk status (e.g., to "In Analysis" or "Pending Approval").' },
      { id: 's5', text: 'Verify the risk heat map updates to reflect changes.' },
    ],
  },
  {
    id: 'bt-09',
    title: 'Vendor Management',
    description: 'Add a vendor, run an assessment, and track contract details.',
    steps: [
      { id: 's1', text: 'Navigate to Vendors from the sidebar.' },
      {
        id: 's2',
        text: 'Click "+ Add Vendor" and fill in vendor name, contact, and contract details.',
      },
      { id: 's3', text: 'Save the vendor and verify it appears in the vendor list.' },
      {
        id: 's4',
        text: 'Open the vendor and start a risk assessment (26-question questionnaire across six sections).',
      },
      { id: 's5', text: 'Answer several assessment questions and save progress.' },
      { id: 's6', text: 'Verify the vendor risk score updates based on your answers.' },
    ],
  },
  {
    id: 'bt-10',
    title: 'Employee Management',
    description: 'Test the employee registry, JML lifecycle, and evaluation features.',
    steps: [
      { id: 's1', text: 'Navigate to Employees from the sidebar.' },
      { id: 's2', text: 'Click "+ Add Employee" and fill in employee details.' },
      { id: 's3', text: 'Save and verify the employee appears in the list.' },
      { id: 's4', text: 'Open the employee and check the JML (Joiner/Mover/Leaver) section.' },
      { id: 's5', text: 'Add a compliance feedback entry (thumbs up/down) for the employee.' },
      { id: 's6', text: 'Verify the feedback is recorded and visible.' },
    ],
  },
  {
    id: 'bt-11',
    title: 'Threat Analysis',
    description: 'Test the AI-assisted threat intelligence module.',
    steps: [
      { id: 's1', text: 'Navigate to Threats from the sidebar (under Risk).' },
      { id: 's2', text: 'Open the Catalogue tab and review any existing threat analyses.' },
      { id: 's3', text: 'Switch to the Agent tab and run a new threat analysis.' },
      { id: 's4', text: 'Review the generated threat details and risk alignment.' },
      {
        id: 's5',
        text: 'Try converting a threat into a risk entry via the "Promote to Risk" action.',
      },
    ],
  },
  {
    id: 'bt-12',
    title: 'Gap Analysis (Prospect View)',
    description: 'Test the gap analysis questionnaire flow as a prospect would experience it.',
    steps: [
      { id: 's1', text: 'Navigate to Assessments under Assurance in the sidebar (route /gap).' },
      { id: 's2', text: 'Click "+ New Assessment" to start a new gap analysis questionnaire.' },
      { id: 's3', text: 'Answer at least 5 questions in the questionnaire.' },
      { id: 's4', text: 'Review the gap analysis results and recommendations.' },
      { id: 's5', text: 'Verify the results are saved and can be revisited.' },
    ],
  },
  {
    id: 'bt-13',
    title: 'Calendar View',
    description: 'Verify the compliance calendar shows tasks and deadlines correctly.',
    steps: [
      { id: 's1', text: 'Navigate to Calendar from the sidebar (under Work).' },
      { id: 's2', text: 'Verify the calendar renders and shows the current month.' },
      { id: 's3', text: 'Check that tasks with due dates appear on the calendar.' },
      { id: 's4', text: 'Click on a calendar event to verify it opens the correct detail view.' },
      { id: 's5', text: 'Navigate to a different month and verify data loads correctly.' },
    ],
  },
  {
    id: 'bt-14',
    title: 'Settings Configuration',
    description: 'Test tenant settings including risk methodology and document types.',
    steps: [
      { id: 's1', text: 'Navigate to Settings from the sidebar.' },
      {
        id: 's2',
        text: 'Open the "Risk Management" tab and review the risk matrix configuration.',
      },
      { id: 's3', text: 'If admin: modify a score threshold and save.' },
      { id: 's4', text: 'Open the "Document Types" tab and verify custom types are listed.' },
      { id: 's5', text: 'Check the "Data Export" tab and try exporting compliance data.' },
    ],
  },
  {
    id: 'bt-15',
    title: 'User Management (Admin)',
    description: 'Test creating, editing, and managing tenant users as an admin.',
    steps: [
      { id: 's1', text: 'Navigate to Users from the sidebar under System (admin only).' },
      { id: 's2', text: 'Review the list of existing users and their roles.' },
      { id: 's3', text: 'Create a new user with the "reader" role.' },
      { id: 's4', text: 'Edit the new user and change their role to "manager".' },
      { id: 's5', text: 'Verify role changes are saved and reflected in the user list.' },
    ],
  },
  {
    id: 'bt-16',
    title: 'Activity Log (Admin)',
    description: 'Verify the activity log records and displays user actions.',
    steps: [
      { id: 's1', text: 'Navigate to Activity Log from the sidebar under System (admin only).' },
      { id: 's2', text: 'Verify recent actions (logins, document saves, etc.) are listed.' },
      { id: 's3', text: 'Use the filter to search by user or module.' },
      { id: 's4', text: 'Check that timestamps and action descriptions are accurate.' },
      {
        id: 's5',
        text: 'Perform an action (e.g., save a document) and verify it appears in the log.',
      },
    ],
  },
  {
    id: 'bt-17',
    title: 'Action Centre',
    description: 'Test the Action Centre modal for pending approvals and obligations.',
    steps: [
      { id: 's1', text: 'Click the Action Centre button (◎) in the sidebar user card.' },
      {
        id: 's2',
        text: 'Review the list of pending items (risk reviews, document approvals, obligations).',
      },
      { id: 's3', text: 'Click on an item to navigate to its detail view.' },
      { id: 's4', text: 'Complete an action (approve a document or address an obligation).' },
      { id: 's5', text: 'Return to Action Centre and verify the badge count decreased.' },
    ],
  },
  {
    id: 'bt-18',
    title: 'Command Palette & Navigation',
    description: 'Test the keyboard-driven command menu for quick navigation.',
    steps: [
      { id: 's1', text: 'Press Ctrl+K (or Cmd+K on Mac) to open the command palette.' },
      { id: 's2', text: 'Type a document name and verify search results appear.' },
      {
        id: 's3',
        text: 'Select a result and verify it navigates to the correct page or opens a modal.',
      },
      { id: 's4', text: 'Open the command palette again and search for a control ID.' },
      { id: 's5', text: 'Close the palette with Escape and verify it dismisses cleanly.' },
    ],
  },
  {
    id: 'bt-19',
    title: 'Contacting Support',
    description: 'Test the support flow — the single way to tell us something is wrong.',
    steps: [
      { id: 's1', text: 'Click "Contact support" in the sidebar user card (or the top-bar menu).' },
      { id: 's2', text: 'Click "Ask a question" and fill in the subject and description.' },
      { id: 's3', text: 'Note whether any knowledge-bank answer is suggested as you type.' },
      { id: 's4', text: 'Submit, and verify a reference (SUP-nnnn) is shown.' },
      { id: 's5', text: 'Verify the question then appears in your list on the Support page.' },
    ],
  },
  {
    id: 'bt-20',
    title: 'Responsive & Theme Behavior',
    description: 'Test the UI at different screen sizes and verify theme consistency.',
    steps: [
      { id: 's1', text: 'Open SeaSuite in a full-width desktop browser.' },
      {
        id: 's2',
        text: 'Navigate through Dashboard, Documents, Tasks, and Risks — verify layouts are clean.',
      },
      {
        id: 's3',
        text: 'Resize the browser to a narrow width (~800px) and check for layout issues.',
      },
      {
        id: 's4',
        text: 'Open user settings and change your theme (if theme options are available).',
      },
      {
        id: 's5',
        text: 'Verify all pages respect the dark theme — no bright/white flashes or unstyled areas.',
      },
      {
        id: 's6',
        text: 'Check that modals, dropdowns, and tooltips render correctly at all sizes.',
      },
    ],
  },
  // ── New tests (added after initial beta launch) ──────────────
  {
    id: 'bt-21',
    title: 'Asset Management',
    isNew: true,
    description:
      'Test the centralised asset register — create, classify, assign, and audit assets.',
    steps: [
      { id: 's1', text: 'Navigate to Assets from the sidebar (under Assets & Parties).' },
      {
        id: 's2',
        text: 'Click "+ Add Asset" and select an asset type (e.g. Hardware, Software, Cloud Service).',
      },
      {
        id: 's3',
        text: 'Fill in the asset name, description, and set a data classification (e.g. Confidential).',
      },
      { id: 's4', text: 'Assign an owner and an assignee from the employee list.' },
      {
        id: 's5',
        text: 'Enable the "Contains personal data" toggle and verify the GDPR/PII badge appears.',
      },
      {
        id: 's6',
        text: 'Set a next audit date in the past and verify the overdue indicator shows.',
      },
      {
        id: 's7',
        text: 'Save the asset and verify it appears in the asset list with correct status and type.',
      },
    ],
  },
  {
    id: 'bt-22',
    title: 'Business Impact Analysis (BIA)',
    isNew: true,
    description:
      'Walk through the BIA wizard to assess criticality, recovery objectives, dependencies, and stakeholders — including auto-save and the draggable severity timeline.',
    steps: [
      { id: 's1', text: 'Navigate to Business Impact from the sidebar (under Resilience).' },
      {
        id: 's2',
        text: 'Click "+ New BIA" to open the wizard. Enter a title and select an asset, then navigate between tabs using the tab headers — verify each tab saves automatically without requiring an explicit Save button.',
      },
      {
        id: 's3',
        text: 'In the Business Context tab, click "+ Add Process" to add a dependent business process, fill in the peak periods, and proceed.',
      },
      {
        id: 's4',
        text: 'In the Impact Assessment tab, drag the handles on the severity timeline to set impact scores across time intervals — verify the overall criticality level auto-calculates and updates as you adjust.',
      },
      {
        id: 's5',
        text: 'In the Recovery tab, enter RTO, RPO, and MTPD values. Verify that plain-language guidance text appears beneath each field to help interpret the values.',
      },
      {
        id: 's6',
        text: 'In the Dependencies tab, add at least one upstream dependency and link it to an existing asset or vendor from the asset/vendor register (not free text).',
      },
      {
        id: 's7',
        text: 'In the Stakeholders tab, assign an owner — confirm that only the BIA owner can make assignment changes (non-owners see the field as read-only).',
      },
      {
        id: 's8',
        text: 'Close the wizard mid-way without completing it — verify a prompt appears asking whether to save progress. Confirm and reopen the BIA to check the data was preserved.',
      },
      {
        id: 's9',
        text: 'Complete the wizard fully and verify the BIA appears in the list with the correct criticality level badge.',
      },
      {
        id: 's10',
        text: 'Change the BIA status from Draft to In Review and verify the status label updates in the list.',
      },
    ],
  },
  {
    id: 'bt-23',
    title: 'Incident Management',
    isNew: true,
    description:
      'Log security incidents, manage the response lifecycle, run IR exercises, and review KPIs.',
    steps: [
      { id: 's1', text: 'Navigate to Incidents from the sidebar (under Resilience).' },
      {
        id: 's2',
        text: 'Click "+ New Incident" and fill in title, type (e.g. Security), and severity (e.g. P2 High).',
      },
      {
        id: 's3',
        text: 'Set the discovery date and verify MTTD appears in the reporting tab later.',
      },
      { id: 's4', text: 'Enable the GDPR breach flag and enter a DPA notification date.' },
      {
        id: 's5',
        text: 'Progress the incident through statuses: Reported → Triaged → In Progress → Contained → Closed.',
      },
      { id: 's6', text: 'Add root cause, lessons learned, and at least one action item.' },
      { id: 's7', text: 'Switch to the IR Exercises tab and create a new tabletop exercise.' },
      {
        id: 's8',
        text: 'Switch to the Reporting tab and verify the KPI dashboard shows MTTD, MTTR, and breach summary.',
      },
    ],
  },
  {
    id: 'bt-24',
    title: 'ROPA — Records of Processing Activities',
    isNew: true,
    description:
      'Build the GDPR Art. 30 register — add processing activities with lawful basis, data subjects, and transfers.',
    steps: [
      { id: 's1', text: 'Navigate to ROPA from the sidebar (under Privacy).' },
      {
        id: 's2',
        text: 'Click "+ Add Entry" and fill in the processing activity name and purpose.',
      },
      { id: 's3', text: 'Select a lawful basis (e.g. Consent or Legitimate Interests).' },
      {
        id: 's4',
        text: 'Add data subject categories (e.g. Employees, Customers) and data categories (e.g. Name, Email).',
      },
      { id: 's5', text: 'Enable the special category (Art. 9) flag and select a condition.' },
      {
        id: 's6',
        text: 'Add a third-country transfer and select the transfer mechanism (e.g. SCCs).',
      },
      { id: 's7', text: 'Set a retention period and next review date, then save.' },
      {
        id: 's8',
        text: 'Verify the entry appears in the list — toggle the Art. 9 and 3rd-country filters.',
      },
      {
        id: 's9',
        text: 'Export the ROPA register to CSV and verify the download contains all entries.',
      },
    ],
  },
  {
    id: 'bt-25',
    title: 'Management Review (ISO 27001 Clause 9.3)',
    isNew: true,
    description:
      'Schedule and conduct management reviews — assign chairs, track action categories, link minutes documents, and verify the completion gate.',
    steps: [
      { id: 's1', text: 'Navigate to Mgt Review from the sidebar (under Governance).' },
      {
        id: 's2',
        text: 'Click "+ New Review" and enter a title, description, and scheduled date.',
      },
      { id: 's3', text: 'Assign a chair from the user list.' },
      {
        id: 's4',
        text: 'Schedule a review within 14 days — verify the urgent indicator appears on the card.',
      },
      {
        id: 's5',
        text: 'Transition the review status to "In Progress" and set the conducted date.',
      },
      {
        id: 's6',
        text: 'Add at least one action item and assign it a category (e.g. Corrective Action, Improvement, or Policy Change) — verify the category label appears on the action item.',
      },
      {
        id: 's7',
        text: 'Link a minutes document to the review using the minutes document link field — verify the linked document is clickable and opens the correct document.',
      },
      {
        id: 's8',
        text: 'Try to mark the review as "Complete" without all required fields filled in — verify the completion gate blocks the transition and shows an explanation.',
      },
      {
        id: 's9',
        text: 'Fill all required fields and mark as "Complete" — verify the stats on the Management Review page update correctly.',
      },
    ],
  },
  {
    id: 'bt-26',
    title: 'Integrations',
    isNew: true,
    description:
      'Browse the integration catalogue, search by category, and review connection details.',
    steps: [
      { id: 's1', text: 'Navigate to Integrations from the sidebar (under System).' },
      { id: 's2', text: 'Browse the integration catalogue and verify providers are displayed.' },
      {
        id: 's3',
        text: 'Use the category filter pills to filter by a specific category (e.g. Asset Management).',
      },
      { id: 's4', text: 'Search for an integration by name (e.g. "Intune" or "ServiceNow").' },
      {
        id: 's5',
        text: 'Click on an integration to open the detail panel and review its description.',
      },
      {
        id: 's6',
        text: 'Verify that "Coming Soon" integrations are visually distinguished from available ones.',
      },
    ],
  },
  // ── Task system deep-dive tests (post-redesign) ─────────────
  {
    id: 'bt-27',
    title: 'Obligation Catalog',
    isNew: true,
    description: 'Browse, enable, and disable compliance obligations from the 45-item catalog.',
    steps: [
      { id: 's1', text: 'Navigate to Tasks and click the Obligation Catalog tab.' },
      {
        id: 's2',
        text: 'Browse the list of obligations — verify names, descriptions, and cadence labels are displayed.',
      },
      {
        id: 's3',
        text: 'Use the category filter pills (e.g. Governance, Technical, People) and verify the list updates.',
      },
      {
        id: 's4',
        text: 'Expand an obligation row to see its details, mapped controls, and auto-evidence strategy.',
      },
      {
        id: 's5',
        text: 'Click "Enable" on a disabled obligation — verify it moves to "Enabled" state with a green badge.',
      },
      {
        id: 's6',
        text: 'Click "Disable" on the same obligation — verify it returns to the disabled state.',
      },
      {
        id: 's7',
        text: 'Enable at least two obligations, then switch to the Obligations tab and verify they appear as materialised tasks.',
      },
    ],
  },
  {
    id: 'bt-28',
    title: 'Obligation Tasks',
    isNew: true,
    description:
      'Review materialised obligation tasks, check fulfilment criteria, and assign owners.',
    steps: [
      {
        id: 's1',
        text: 'Navigate to Tasks and click the Obligations tab (ensure at least one obligation is enabled).',
      },
      {
        id: 's2',
        text: 'Verify obligation tasks are listed with title, status (Unmet/Overdue), cadence, and due date.',
      },
      {
        id: 's3',
        text: 'Use the status filter (All, Unmet, Overdue) and verify the list filters correctly.',
      },
      { id: 's4', text: 'Use the assignee filter (All, Mine, Unassigned) and verify it works.' },
      { id: 's5', text: 'Click on an obligation task to open the detail modal.' },
      {
        id: 's6',
        text: 'Review the fulfilment criteria checklist — verify each criterion shows pass/fail status.',
      },
      {
        id: 's7',
        text: 'Change the assignee in the detail modal and verify it saves (the dropdown updates).',
      },
      {
        id: 's8',
        text: 'If a domain action is available (e.g. "Approve document" or "Upload artefact"), click it and verify it navigates correctly.',
      },
    ],
  },
  {
    id: 'bt-29',
    title: 'Risk Actions',
    isNew: true,
    description:
      'Test domain-backed actions that originate from risks — view, complete, and track progress.',
    steps: [
      {
        id: 's1',
        text: 'Navigate to Risks and open a risk that has actions (or create a new risk and add actions in the wizard).',
      },
      {
        id: 's2',
        text: 'In the risk detail modal, verify the Actions section shows each action with status badges and a progress bar.',
      },
      {
        id: 's3',
        text: 'Click on an action row in the risk detail to open the Domain Action Detail modal.',
      },
      {
        id: 's4',
        text: 'Verify the action shows its source risk, status, assignee, and due date.',
      },
      {
        id: 's5',
        text: 'Click "Mark complete" and verify the status changes to Completed with a timestamp.',
      },
      {
        id: 's6',
        text: 'Navigate to Tasks → Actions tab and verify the completed action is reflected there too.',
      },
      {
        id: 's7',
        text: 'Check that the risk detail progress bar updated after completing the action.',
      },
    ],
  },
  {
    id: 'bt-30',
    title: 'Ad-Hoc Tasks',
    isNew: true,
    description: 'Create, edit, transition, and delete manual ad-hoc compliance tasks.',
    steps: [
      { id: 's1', text: 'Navigate to Tasks and click the Ad-Hoc tab.' },
      {
        id: 's2',
        text: 'Click "+ New Task" and fill in title, description, due date, and assignee.',
      },
      { id: 's3', text: 'Link one or more controls using the control picker.' },
      {
        id: 's4',
        text: 'Save the task and verify it appears in the ad-hoc task list with "Open" status.',
      },
      {
        id: 's5',
        text: 'Open the task detail modal and change status from "Open" to "In Progress".',
      },
      {
        id: 's6',
        text: 'Change status from "In Progress" to "Closed" and verify the badge updates.',
      },
      { id: 's7', text: 'Reopen the task (Closed → Open) and verify it returns to Open status.' },
      {
        id: 's8',
        text: 'Delete the task — confirm the deletion prompt and verify the task is removed from the list.',
      },
    ],
  },
  {
    id: 'bt-31',
    title: 'Artefact Register',
    isNew: true,
    description:
      'Create, classify, and manage compliance artefacts (evidence) in the centralised register.',
    steps: [
      { id: 's1', text: 'Navigate to Artefacts from the sidebar (under Governance).' },
      { id: 's2', text: 'Click "+ New Artefact" and fill in the title and description.' },
      {
        id: 's3',
        text: 'Select an artefact type (File, Screenshot, Attestation, Report, or Other).',
      },
      { id: 's4', text: 'Link one or more controls using the control picker.' },
      { id: 's5', text: 'Add a domain reference (e.g. link to a Risk or Document).' },
      {
        id: 's6',
        text: 'Save the artefact and verify it appears in the list with the correct type badge.',
      },
      {
        id: 's7',
        text: 'Use the type filter pills to filter by a specific type and verify filtering works.',
      },
      { id: 's8', text: 'Use the search bar to find the artefact by title and verify it appears.' },
      {
        id: 's9',
        text: 'Open the artefact detail, edit a field (e.g. description), save, and verify the change persists.',
      },
    ],
  },
  {
    id: 'bt-32',
    title: 'Statement of Applicability (SoA)',
    isNew: true,
    description:
      'Manage the SOA — set applicability, implementation status, exclusion justifications, and take snapshots.',
    steps: [
      { id: 's1', text: 'Navigate to Applicability from the sidebar (under Governance).' },
      {
        id: 's2',
        text: 'Verify the control list loads with framework labels, applicability toggles, and implementation status.',
      },
      {
        id: 's3',
        text: 'Toggle a control to "Not Applicable" and enter an exclusion justification.',
      },
      {
        id: 's4',
        text: 'Change the implementation status of a control (e.g. Not Started → In Progress).',
      },
      {
        id: 's5',
        text: 'Save a version snapshot — enter a version label in the save dialog and confirm.',
      },
      { id: 's6', text: 'Verify the version appears in the version history list.' },
      {
        id: 's7',
        text: 'Export the SOA to PDF or Excel and verify the download contains the correct data.',
      },
    ],
  },
  // ── BCDR, Vulnerabilities, Security Events ──────────────────
  {
    id: 'bt-33',
    title: 'BC/DR — Business Continuity & Disaster Recovery',
    isNew: true,
    description:
      'Test the BCDR module — create continuity plans, runbooks, backup records, DR tests, and pen test entries.',
    steps: [
      {
        id: 's1',
        text: 'Navigate to BC/DR from the sidebar (under Resilience). Verify the six tabs are present: Dashboard, Plans, Runbooks, Backups, DR Tests, and Pen Tests.',
      },
      {
        id: 's2',
        text: 'In the Plans tab, click "+ New Plan" and create a Business Continuity Plan (BCP) — fill in title, type, and owner. Save and verify it appears in the list with Draft status.',
      },
      {
        id: 's3',
        text: 'In the Runbooks tab, click "+ New Runbook" and create an active runbook. Save and verify it appears in the list.',
      },
      {
        id: 's4',
        text: 'In the Backups tab, click "+ New Backup" and record a backup job — fill in system name, frequency, and last backup result (Success/Partial/Fail). Save and verify it appears.',
      },
      {
        id: 's5',
        text: 'In the DR Tests tab, click "+ New DR Test" and schedule a tabletop exercise — fill in scope, date, and participants. Save and verify it appears with Planned status.',
      },
      {
        id: 's6',
        text: 'In the Pen Tests tab, click "+ New Pen Test" and create a pen test entry — fill in type (e.g. External), scope, and scheduled date. Save and verify it appears.',
      },
      {
        id: 's7',
        text: 'Return to the Dashboard tab and verify the summary stats (active plans, runbooks, backup health, upcoming tests) reflect the records you just created.',
      },
    ],
  },
  {
    id: 'bt-34',
    title: 'Vulnerabilities',
    isNew: true,
    description:
      'Review the vulnerability register sourced from Defender/CVE correlation — triage findings, apply overrides, and promote to risks.',
    steps: [
      { id: 's1', text: 'Navigate to Vulnerabilities from the sidebar (under Risk).' },
      {
        id: 's2',
        text: 'Verify the vulnerability list loads — each row should show severity tier (P1–P4), CVE ID, affected asset, and status.',
      },
      {
        id: 's3',
        text: 'Use the severity filter pills (P1 Critical, P2 High, etc.) to narrow the list and verify filtering works.',
      },
      {
        id: 's4',
        text: 'Click on a vulnerability to open the detail modal. Review the CVE details, affected software, and asset correlation confidence score.',
      },
      {
        id: 's5',
        text: 'In the detail modal, triage the vulnerability — change its status to "Accepted" or "Deferred" and enter a justification. Save and verify the status updates in the list.',
      },
      {
        id: 's6',
        text: 'For a high-severity open vulnerability, click "Promote to Risk" and verify it opens the Risk Wizard pre-populated with the CVE details.',
      },
    ],
  },
  {
    id: 'bt-35',
    title: 'Security Events Log (Admin)',
    isNew: true,
    description:
      'Review the security events log for auth-channel activity — logins, magic links, password resets, and access grants.',
    steps: [
      { id: 's1', text: 'Navigate to Security Events from the sidebar under System (admin only).' },
      {
        id: 's2',
        text: 'Verify the events list loads and shows recent auth-channel events (logins, failed login attempts, magic-link sends, invite acceptances).',
      },
      {
        id: 's3',
        text: 'Use the type/source filter to narrow to login events only — verify the filter works and the list updates.',
      },
      {
        id: 's4',
        text: 'Check that each event row shows a timestamp, event type, user or email, and IP/source where applicable.',
      },
      {
        id: 's5',
        text: 'Perform a logout and log back in — verify a new login event appears at the top of the Security Events list.',
      },
    ],
  },
  // ── Additional feature tests ────────────────────────────────
  {
    id: 'bt-36',
    title: 'Audits & Findings',
    isNew: true,
    description:
      'Manage audit programmes, log findings with CAPA type and root cause, enforce the effectiveness gate, track audit evidence requests as ordinary tasks, and explore all four tabs.',
    steps: [
      {
        id: 's1',
        text: 'Navigate to Audits & Findings from the sidebar (under Assurance). Verify the four tabs are present: Findings Register, Audit Programme, Internal Audits, and Evidence Requests.',
      },
      {
        id: 's2',
        text: 'In the Findings Register tab, create a new finding — fill in title, severity (Major NC), source (Internal Audit), CAPA type (Corrective Action), and root cause category.',
      },
      {
        id: 's3',
        text: 'Assign an owner, set a target remediation date, and save. Verify the finding appears in the list with the correct severity and CAPA type.',
      },
      {
        id: 's4',
        text: 'Use the severity, status, and CAPA type filter dropdowns to narrow the list — verify all three filters work.',
      },
      {
        id: 's5',
        text: 'Open the finding detail and progress the status: Open → In Remediation → Pending Verification.',
      },
      {
        id: 's6',
        text: 'Try to close the finding without completing the effectiveness assessment — verify the effectiveness gate blocks the status transition and shows an explanation.',
      },
      {
        id: 's7',
        text: 'Complete the effectiveness assessment, then close the finding. Verify it moves to Closed status.',
      },
      {
        id: 's8',
        text: 'Switch to the Audit Programme tab and create or review an audit programme entry.',
      },
      {
        id: 's9',
        text: 'Switch to the Internal Audits tab and verify audit records are listed (or an empty state if none exist).',
      },
      {
        id: 's10',
        text: 'Switch to the Evidence Requests tab and click "Seed from catalogue" — select a handful of items and add them. Verify they appear in the register with a readiness of "Outstanding".',
      },
      {
        id: 's11',
        text: 'Open one evidence item. Confirm there is NO status dropdown on the item itself — readiness at the top is derived. Add a task, assign it to yourself and set a due date.',
      },
      {
        id: 's12',
        text: 'Attach a document and an artefact to the item, then mark its task complete. Verify the readiness chip flips to "Satisfied" and the attached count updates.',
      },
      {
        id: 's13',
        text: 'Mark a different item "Not applicable" with a justification — verify it shows as N/A, has no outstanding task, and still counts as ready in the register summary strip.',
      },
      {
        id: 's14',
        text: 'Go to Tasks → Domain Actions and verify your evidence-request task appears under an "Evidence Requests" section. Open it and confirm you can complete it from there too.',
      },
      {
        id: 's15',
        text: 'Open the Action Centre and verify the evidence task assigned to you appears in your task list (not in a separate evidence bucket). Check the Compliance Calendar shows it on its due date.',
      },
      {
        id: 's16',
        text: 'If your tenant has an Entra ID or Intune integration, seed an auto-checked item (e.g. "MFA Enabled for All Users") and verify it reports Auto-satisfied or Auto-check failing with an explanation — and that an undeterminable check never shows as ready.',
      },
    ],
  },
  {
    id: 'bt-37',
    title: 'Executive View',
    isNew: true,
    description:
      'Review the executive dashboard — posture scores, risk trends, and board brief export.',
    steps: [
      {
        id: 's1',
        text: 'Navigate to Dashboard. As an admin, click the Executive toggle in the dashboard header to switch to Executive view.',
      },
      { id: 's2', text: 'Verify the security posture score widget loads with a numeric score.' },
      { id: 's3', text: 'Check that the risk trend chart renders with historical data points.' },
      {
        id: 's4',
        text: 'Review the framework scorecard — verify it shows compliance percentages per framework.',
      },
      {
        id: 's5',
        text: 'Click "Generate Board Brief" and verify the AI-generated PDF download is produced.',
      },
      {
        id: 's6',
        text: 'Resize the browser and verify the executive dashboard remains readable at narrower widths.',
      },
    ],
  },
  {
    id: 'bt-38',
    title: 'Security Awareness Training',
    isNew: true,
    description:
      'Create training modules from built-in templates, assign them to employees, and complete a quiz via the training player.',
    steps: [
      { id: 's1', text: 'Navigate to Employees from the sidebar, then open the Training tab.' },
      {
        id: 's2',
        text: 'Click "+ New Module" and choose "Import from template". Select a built-in template (e.g. Phishing Awareness) and verify it populates the module with slides and quiz questions.',
      },
      {
        id: 's3',
        text: 'Edit the module if needed, then save. Verify it appears in the training module list with the correct title and estimated duration.',
      },
      {
        id: 's4',
        text: 'Click "Assign" on the module and assign it to one or more employees. Verify the assignment is recorded.',
      },
      {
        id: 's5',
        text: 'Click "Preview" (or "Launch") on the module to open the Training Player. Step through the slides and verify navigation (Next / Back) works correctly.',
      },
      {
        id: 's6',
        text: 'Reach the quiz section. Answer all questions and submit. Verify the player shows pass/fail based on the pass threshold.',
      },
      {
        id: 's7',
        text: 'Return to the training module list and verify the completion status for the assigned employee updated.',
      },
    ],
  },
  {
    id: 'bt-39',
    title: 'User Access Review (UAR)',
    isNew: true,
    description:
      'Create a UAR campaign, activate it, complete reviewer decisions, and generate an evidence report.',
    steps: [
      {
        id: 's1',
        text: 'Navigate to Employees from the sidebar, then open the Access Reviews (UAR) tab.',
      },
      {
        id: 's2',
        text: 'Click "+ New Campaign" and fill in the campaign title, description, scope (All employees or a department), reviewers, and deadline.',
      },
      {
        id: 's3',
        text: 'Save the campaign and verify it appears in the campaign list with Draft status.',
      },
      {
        id: 's4',
        text: 'Activate the campaign — verify it transitions to Active status and the assigned reviewers are notified (or a notification prompt appears).',
      },
      {
        id: 's5',
        text: 'Open the campaign detail and review the employee list. For each subject, make a decision: Approve (access confirmed) or Revoke (access to be removed).',
      },
      {
        id: 's6',
        text: 'Submit all reviewer decisions and close the campaign. Verify the campaign status changes to Closed.',
      },
      {
        id: 's7',
        text: 'Download or view the evidence report for the closed campaign — verify it lists all subjects with their decisions and timestamps.',
      },
    ],
  },
  {
    id: 'bt-40',
    title: 'Dashboard Customization',
    isNew: true,
    description:
      'Add, remove, and rearrange dashboard widgets to personalise the compliance overview.',
    steps: [
      { id: 's1', text: 'Navigate to Dashboard from the sidebar.' },
      {
        id: 's2',
        text: 'Click the customise/edit button on the dashboard (pencil or "Customise" button). Verify the dashboard enters edit mode.',
      },
      {
        id: 's3',
        text: 'Click "+ Add Widget" (or the widget palette icon). Browse the available widget categories and add a widget that is not currently on your dashboard.',
      },
      {
        id: 's4',
        text: 'Verify the new widget appears on the dashboard. Drag it to a different position and verify the layout updates.',
      },
      {
        id: 's5',
        text: 'Remove an existing widget using its remove/close button. Verify it disappears from the dashboard.',
      },
      {
        id: 's6',
        text: 'Save the layout and reload the page — verify the customised layout persists after reload.',
      },
      {
        id: 's7',
        text: 'Check the Continuous Improvement widget (if present) — verify it shows improvement metrics (e.g. closed findings, completed actions) without errors.',
      },
    ],
  },
  {
    id: 'bt-41',
    title: 'GDPR Privacy Suite',
    isNew: true,
    description:
      'Test the Privacy module — DPIA workflow, DSR tracker, Consent register, Legal register, and DPO designation.',
    steps: [
      {
        id: 's1',
        text: 'Navigate to Privacy in the sidebar. Verify the sub-items: ROPA, DPIA, DSR Tracker, Consent, Legal Register, DPO.',
      },
      {
        id: 's2',
        text: 'Open DPIA — click "+ New DPIA" and fill in the processing activity name, purpose, and necessity assessment. Save and verify it appears in the list.',
      },
      {
        id: 's3',
        text: 'In the DPIA, progress the workflow through its stages (e.g. Scoping → Assessment → Review) and verify status changes are saved.',
      },
      {
        id: 's4',
        text: 'Open DSR Tracker — click "+ New Request" and fill in the requestor name, request type (e.g. Access, Erasure, Portability), and received date. Verify it appears in the list and a response deadline is calculated.',
      },
      {
        id: 's5',
        text: 'Open Consent — add a consent record with a data subject, purpose, and consent date. Verify it appears with status "Active".',
      },
      {
        id: 's6',
        text: 'Open Legal Register — add a legal obligation entry (e.g. GDPR Article 30). Verify it saves and appears in the list.',
      },
      {
        id: 's7',
        text: 'Open DPO — enter or update the DPO designation details (name, contact, appointment date). Save and verify the details persist.',
      },
    ],
  },
  {
    id: 'bt-42',
    title: 'Frameworks Manager',
    isNew: true,
    description:
      'Browse, add, and manage compliance framework libraries and their control mappings.',
    steps: [
      { id: 's1', text: 'Navigate to Frameworks from the sidebar (under System).' },
      {
        id: 's2',
        text: 'Verify the framework list loads with standard/law categorisation and control counts.',
      },
      { id: 's3', text: 'Click on a framework to open the detail view and review its controls.' },
      {
        id: 's4',
        text: 'Check that implementation status tracking is visible for each control in the framework.',
      },
      {
        id: 's5',
        text: 'If available, add a new framework or toggle an existing framework on/off.',
      },
      { id: 's6', text: 'Return to the framework list and verify changes are reflected.' },
    ],
  },
];

/** IDs of tests added after the initial beta launch */
export const NEW_TEST_IDS = new Set([
  'bt-21',
  'bt-22',
  'bt-23',
  'bt-24',
  'bt-25',
  'bt-26',
  'bt-27',
  'bt-28',
  'bt-29',
  'bt-30',
  'bt-31',
  'bt-32',
  'bt-33',
  'bt-34',
  'bt-35',
  'bt-36',
  'bt-37',
  'bt-38',
  'bt-39',
  'bt-40',
  'bt-41',
  'bt-42',
]);

export default BETA_TESTS;
